/**
 * NARUTO CARD GAME - Complete Game Configuration
 *
 * This is the ONLY file you need to modify for Naruto-specific settings.
 * Everything about the game is configured here.
 */

import { NarutoHandler } from "./handler";
import {
  getCardData,
  getAACardIdData,
  getCardImage,
  getAAFromCardId,
} from "@/lib/games/naruto/getCardDataLocal";
import { CDN } from "@/lib/cdn";
import { GAME_DOMAINS } from "@/games/domains";

export const narutoGame = {
  // ============================================================================
  // BASIC INFO
  // ============================================================================
  id: "naruto" as const,
  name: "Naruto TCG",
  abbreviation: "Naruto TCG",
  gameId: 1000, // Database game ID

  // ============================================================================
  // SEO & METADATA
  // ============================================================================
  seo: {
    title: "Naruto TCG Meta Decklists & Card DB",
    description:
      "Dive into the world of the Naruto TCG with TCGfan. Browse competitive meta decklists, explore a comprehensive card database, and discover the latest strategies featuring your favorite shinobi. Whether you're a veteran player mastering the ninja way or a newcomer building your first deck, TCGfan provides all the tools you need to excel in the Naruto TCG.",
  },

  // ============================================================================
  // GAME MECHANICS
  // ============================================================================
  mechanics: {
    hasLeader: true,
    hasLeaderDistribution: true,
    hasPieChart: true,
    hasFormatReleaseDates: false,
    maxCopiesDefault: 3,
    hasLifeProperty: false,
    leaderType: "Ninja",
  },

  tcgplayer: undefined as
    { categoryId: number; productLine: string } | undefined,

  routePrefix: "naruto/",

  // ============================================================================
  // FORMATS & SETS
  // ============================================================================
  formats: {
    global: ["DEMO"],
    east: [], // Keep east as empty array for backward compatibility
  },

  /** Public decklist-submission form, if the game accepts submissions. */
  submitFormUrl: undefined as string | undefined,

  // ============================================================================
  // NAVIGATION & UI
  // ============================================================================
  navigation: {
    /** The game's deck builder, or undefined if it has none yet. */
    deckbuilderHref: undefined as string | undefined,
    showMarketWatch: true,
    marketWatchHref: "/naruto/market-watch",
    showTools: false,
    showBoosters: false,
    showStarters: false,
    showSearch: false,
  },

  // ============================================================================
  // DOMAIN CONFIGURATION
  // ============================================================================
  domain: {
    ...GAME_DOMAINS["naruto"],
    disabledRoutes: [] as string[],
  },

  // ============================================================================
  // GAME HANDLER & DECK BUILDER
  // ============================================================================
  handler: () => new NarutoHandler(),

  deckBuilder: {
    getCardData,
    getAACardIdData,
    getCardImage,
    getAAFromCardId,
  },

  // ============================================================================
  // CDN CONFIGURATION
  // ============================================================================
  cdn: {
    leaderSquaresPath: `${CDN}/games/naruto/images/leader-squares`,
  },

  // ============================================================================
  // INITIAL STATE FOR STORES (auto-generated, no need to modify)
  // ============================================================================
  initialState: {
    prices: null,
    loading: false,
    decks: [],
    cardOrderIds: [],
    filters: {},
  },
};

export type NarutoGame = typeof narutoGame;
